import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { format, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { facilityApi, getFacilityClasses, memberApi, eventApi } from '../api';
import { reservationApi } from '../reservation_api';
import { Court, BusinessUser, Class, RecurringEvent } from '../api_types';
import { courtApi, businessUserApi, recurringEventApi, staffApi, StaffUser } from '../api';
import NewCourtReservationModal from './CourtReservationModal';
import './styles/NewCourtScheduler.css';
import { useAuth } from '../contexts/AuthContext';
import DateBlockerModal from './DateBlockerModal';
import LoadingSpinner from '../shared/components/LoadingSpinner';
import { websocketService } from '../services/websocket';
import NotificationCounter from '../shared/components/NotificationCounter';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface ScheduleSlot {
  id: string;
  courtId: string;
  date: string;
  startTime: string;
  endTime: string;
  status: 'available' | 'booked' | 'blocked';
  notes?: string;
  assignedTo?: string | null;
  assignedUserDetails?: {
    id: string;
    name: string;
    email: string;
    role: string;
  } | null;
  isEvent?: boolean;
  eventTitle?: string;
  title: string;
  reservationName?: string;
  isRecurring?: boolean;
  recurrenceRule?: any;
  isClass?: boolean;
  capacity?: number;
  price?: number;
  registeredMembers?: string[];
  className?: string;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface AssignableUser {
  id: string;
  name: string;
  email: string;
  role: string;
  type: 'business' | 'staff' | 'member';
}

interface TimeSlotOption {
  startTime: string;
  duration: '30min' | '1hour';
  isRecommended: boolean;
}

interface FacilityEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  courtIds: string[];
  capacity?: number;
  registrationRequired: boolean;
  registrationDeadline?: string;
  price?: number;
  status: 'scheduled' | 'cancelled' | 'completed';
}

interface NewCourtSchedulerProps {
  facilityId: string;
  businessId: string;
}

interface BusinessHours {
  [key: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
  sunday: { isClosed: boolean; openTime: string; closeTime: string };
  monday: { isClosed: boolean; openTime: string; closeTime: string };
  tuesday: { isClosed: boolean; openTime: string; closeTime: string };
  wednesday: { isClosed: boolean; openTime: string; closeTime: string };
  thursday: { isClosed: boolean; openTime: string; closeTime: string };
  friday: { isClosed: boolean; openTime: string; closeTime: string };
  saturday: { isClosed: boolean; openTime: string; closeTime: string };
}

interface Notification {
  id: string;
  message: string;
  timestamp: Date;
}

const formatTimeForInput = (time: string) => {
  const [timePart, period] = time.split(' ');
  const [hours, minutes] = timePart.split(':');
  let hour = parseInt(hours);
  if (period === 'PM' && hour < 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

const NewCourtScheduler: React.FC<NewCourtSchedulerProps> = ({ facilityId, businessId }) => {
  const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleSlot | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    courtId: string;
    time: string;
  } | null>(null);
  const [showNewReservationModal, setShowNewReservationModal] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState< '30min' | '1hour'>('1hour');
  const [events, setEvents] = useState<FacilityEvent[]>([]);
  const [scrollInitialized, setScrollInitialized] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHours>({
    sunday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    monday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    tuesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    wednesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    thursday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    friday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
    saturday: { isClosed: false, openTime: '07:00', closeTime: '21:00' }
  });
  const [recurringEvents, setRecurringEvents] = useState<RecurringEvent[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const { user } = useAuth();
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  const [showDateBlockerModal, setShowDateBlockerModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSmartModal, setShowSmartModal] = useState(true);
  const [forceShowGrid, setForceShowGrid] = useState(false);
  const queryClient = useQueryClient();
  const [usersLoading, setUsersLoading] = useState(true);

  const { data: courts = [], isLoading: courtsLoading } = useQuery<Court[]>({
    queryKey: ['courts', facilityId, businessId],
    queryFn: async () => {
      const data = await courtApi.getCourts(businessId, facilityId);
      return data.sort((a, b) => {
        const aMatch = a.name.match(/\d+/);
        const bMatch = b.name.match(/\d+/);
        const aNum = aMatch ? parseInt(aMatch[0]) : 0;
        const bNum = bMatch ? parseInt(bMatch[0]) : 0;
        if (aNum === bNum) return a.name.localeCompare(b.name);
        return aNum - bNum;
      });
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const fetchSchedules = useCallback(async () => {
    if (!selectedDate) return [];
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      // Fetch regular schedules
      const schedulesData = await reservationApi.getMemberSchedule(facilityId, dateStr, businessId);
      // Fetch classes for the selected date
      const facilityClasses = await getFacilityClasses(businessId, facilityId);
      const classesForDate = facilityClasses.filter(
        classItem => classItem.date === dateStr && classItem.status === 'scheduled'
      );
      setClasses(classesForDate);
      // Convert classes to schedule slots
      const classSlots = classesForDate.flatMap(classItem => {
        return classItem.courtIds.map((courtId: string) => ({
          id: `${classItem.id}-${courtId}`,
          courtId,
          date: classItem.date,
          startTime: classItem.startTime,
          endTime: classItem.endTime,
          status: 'blocked' as const,
          notes: classItem.description,
          assignedTo: null,
          assignedUserDetails: null,
          isEvent: true,
          isClass: true,
          eventTitle: classItem.title,
          title: classItem.title,
          capacity: classItem.maxCapacity,
          price: classItem.price,
          registeredMembers: classItem.registeredMembers
        }));
      });
      // Fetch events for the selected date
      const facilityEventsData = await eventApi.getFacilityEventsForDate(businessId, facilityId, dateStr);
      // Convert facility events to schedule slots
      const eventSlots = facilityEventsData
        .filter(event => event.status === 'scheduled' && event.date === dateStr)
        .flatMap(event => event.courtIds.map(courtId => ({
          id: `${event.id}-${courtId}`,
          courtId,
          date: event.date,
          startTime: event.startTime,
          endTime: event.endTime,
          status: 'blocked' as const,
          notes: event.description,
          assignedTo: null,
          assignedUserDetails: null,
          isEvent: true,
          isRecurring: false,
          eventTitle: event.title,
          title: event.title
        })));
      
      // Fetch recurring event occurrences for the selected date
      const recurringEventOccurrences = await recurringEventApi.getRecurringEventOccurrencesByDate(businessId, facilityId, dateStr);
      
      // Convert recurring event occurrences to schedule slots
      const recurringEventSlots = recurringEventOccurrences
        .filter(occurrence => occurrence.status === 'scheduled')
        .flatMap(occurrence => 
          occurrence.courtIds.map(courtId => ({
            id: `${occurrence.parentEventId}-${courtId}-${dateStr}`,
            courtId,
            date: dateStr,
            startTime: occurrence.startTime,
            endTime: occurrence.endTime,
            status: 'blocked' as const,
            notes: occurrence.description,
            assignedTo: null,
            assignedUserDetails: null,
            isEvent: true,
            isRecurring: true,
            eventTitle: occurrence.title,
            title: occurrence.title,
            capacity: occurrence.capacity,
            price: occurrence.price,
            registrationRequired: occurrence.registrationRequired,
            registrationDeadline: occurrence.registrationDeadline
          }))
        );
      
      setEvents(facilityEventsData);
      // Prioritize blocked events so they overlay all other slots
      const oneTimeSlots = eventSlots;
      const blockedOneTimeSlots = oneTimeSlots.filter(s => s.eventTitle === 'Blocked');
      const otherOneTimeSlots = oneTimeSlots.filter(s => s.eventTitle !== 'Blocked');
      const recurringSlots = recurringEventSlots;
      const blockedRecurringSlots = recurringSlots.filter(s => s.eventTitle === 'Blocked');
      const otherRecurringSlots = recurringSlots.filter(s => s.eventTitle !== 'Blocked');
      return [
        ...blockedOneTimeSlots,
        ...blockedRecurringSlots,
        ...schedulesData,
        ...classSlots,
        ...otherOneTimeSlots,
        ...otherRecurringSlots
      ];
    } catch (err) {
      console.error('Error fetching schedules:', err);
      return [];
    }
  }, [facilityId, selectedDate, businessId]);

  // Schedules: use React Query
  const { data: schedules = [], isLoading: schedulesLoading, refetch: refetchSchedules } = useQuery<ScheduleSlot[]>({
    queryKey: ['schedules', facilityId, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: fetchSchedules,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Notifications: use React Query
  const { data: notificationsRaw = [] } = useQuery<Notification[]>({
    queryKey: ['notifications', facilityId],
    queryFn: async () => queryClient.getQueryData<Notification[]>(['notifications', facilityId]) || [],
  });
  const notifications = notificationsRaw.map(n => ({
    ...n,
    timestamp: typeof n.timestamp === 'string' ? new Date(n.timestamp) : n.timestamp,
  }));
  const addNotification = (message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newNotification = { id, message, timestamp: new Date() };
    queryClient.setQueryData(['notifications', facilityId], () => [newNotification]);
  };
  const dismissNotification = (id: string) => {
    console.log('Dismissing notification:', id);
    queryClient.setQueryData(['notifications', facilityId], (prev: Notification[] = []) => {
      const filtered = prev.filter(n => n.id !== id);
      console.log('After filter:', filtered);
      return filtered;
    });
  };

  const newLoadBusinessHours = useCallback(async () => {
    console.log('Loading business hours for:', { businessId, facilityId });
    const facility_hours = await facilityApi.getFacilityBusinessHours(businessId, facilityId);
    console.log('Loaded business hours:', facility_hours);
    
    // Convert string format to object format
    const convertedHours: BusinessHours = { ...businessHours };
    Object.entries(facility_hours as unknown as Record<string, string>).forEach(([day, hours]) => {
      if (typeof hours === 'string') {
        if (hours === 'Closed') {
          convertedHours[day as keyof BusinessHours] = { isClosed: true, openTime: '07:00', closeTime: '21:00' };
        } else {
          const [openTime, closeTime] = hours.split(' - ').map((t: string) => formatTimeForInput(t));
          convertedHours[day as keyof BusinessHours] = { isClosed: false, openTime, closeTime };
        }
      }
    });
    
    console.log('Converted business hours:', convertedHours);
    setBusinessHours(convertedHours);
  }, [businessId, facilityId]);

  // Load business hours
  useEffect(() => {
    console.log('Effect triggered with:', { businessId, facilityId });
    newLoadBusinessHours();
  }, [businessId, facilityId, newLoadBusinessHours]);

  // Move TIME_SLOTS inside component and make it dependent on business hours
  const TIME_SLOTS = useMemo(() => {
    console.log('Generating TIME_SLOTS with business hours:', businessHours);
    const slots: Array<{ display: string; value: string; isClosing?: boolean }> = [];
    const dayOfWeek = selectedDate.getDay();
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][dayOfWeek];
    const hours = businessHours[dayName];
    console.log('Current day hours:', { dayName, hours });

    if (!hours || hours.isClosed || !hours.openTime || !hours.closeTime) {
      console.log('No valid hours found for day:', dayName);
      return [];
    }

    const [openHour, openMinute] = hours.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = hours.closeTime.split(':').map(Number);

    for (let hour = openHour; hour <= closeHour; hour++) {
      const startMinute = hour === openHour ? openMinute : 0;
      const endMinute = hour === closeHour ? closeMinute : 60;
      for (let minute = startMinute; minute < endMinute; minute += 30) {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        slots.push({
          display: `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`,
          value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    // Add extra closing slot if not already present
    const closingPeriod = closeHour >= 12 ? 'PM' : 'AM';
    const closingDisplayHour = closeHour > 12 ? closeHour - 12 : closeHour === 0 ? 12 : closeHour;
    const closingDisplay = `${closingDisplayHour}:${closeMinute.toString().padStart(2, '0')} ${closingPeriod}`;
    const closingValue = `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`;
    // Only add if not already last
    if (slots.length === 0 || slots[slots.length - 1].value !== closingValue) {
      slots.push({ display: closingDisplay, value: closingValue, isClosing: true });
    } else {
      // Mark the last slot as closing if it matches
      slots[slots.length - 1].isClosing = true;
    }
    return slots;
  }, [businessHours, selectedDate]);

  const fetchBusinessUsers = useCallback(async () => {
    try {
      const facilityUsers = await businessUserApi.getBusinessUsers(businessId);
      setBusinessUsers(facilityUsers);
      return facilityUsers;
    } catch (err) {
      console.error('Error fetching business users:', err);
      return [];
    }
  }, [facilityId]);

  const fetchStaffUsers = useCallback(async () => {
    try {
      const all = await staffApi.getStaffUsers(facilityId);
      const facilityStaff = all.filter((u) => u.facility_id === facilityId);
      setStaffUsers(facilityStaff);
      return facilityStaff;
    } catch (err) {
      console.error('Error fetching staff users:', err);
      return [];
    }
  }, [facilityId]);

  const fetchMembers = useCallback(async () => {
    try {
      const membersData = await memberApi.getFacilityMembers(businessId, facilityId);
      // Map FacilityMember to Member, adding a default role
      const mappedMembers = membersData.map(m => ({
        ...m,
        role: 'member',
      }));
      setMembers(mappedMembers);
      return mappedMembers;
    } catch (err) {
      console.error('Error fetching members:', err);
      return [];
    }
  }, [facilityId]);

  // Fetch base data (courts, users, members)
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setUsersLoading(true);
    try {
      await Promise.all([
        fetchBusinessUsers(),
        fetchStaffUsers(),
        fetchMembers(),
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
    }
    setLoading(false);
    setUsersLoading(false);
  }, [fetchBusinessUsers, fetchStaffUsers, fetchMembers]);

  // Call fetchInitialData on mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Schedule and events load
  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Update assignable users when business users or members change
  useEffect(() => {
    const businessAssignableUsers: AssignableUser[] = businessUsers.map(bu => ({
      id: bu.id,
      name: bu.name,
      email: bu.email,
      role: bu.role,
      type: 'business'
    }));

    const staffAssignableUsers: AssignableUser[] = staffUsers.map(su => ({
      id: su.id,
      name: su.name,
      email: su.email,
      role: su.role,
      type: 'staff'
    }));

    const memberAssignableUsers: AssignableUser[] = members.map(m => ({
      id: m.id,
      name: `${m.firstName} ${m.lastName}`,
      email: m.email,
      role: m.role,
      type: 'member'
    }));

    setAssignableUsers([...businessAssignableUsers, ...staffAssignableUsers, ...memberAssignableUsers]);
  }, [businessUsers, staffUsers, members]);

  const getSlotStatus = useCallback((courtId: string, time: string) => {
    const slot = (schedules as ScheduleSlot[]).find((s: ScheduleSlot) => {
      // Convert times to minutes since midnight for easier comparison
      const slotTime = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      const startTime = parseInt(s.startTime.split(':')[0]) * 60 + parseInt(s.startTime.split(':')[1]);
      const endTime = parseInt(s.endTime.split(':')[0]) * 60 + parseInt(s.endTime.split(':')[1]);
      
      return s.courtId === courtId && 
             slotTime >= startTime && 
             slotTime < endTime;
    });

    if (!slot) {
      return { status: 'available', details: null, reservation: null, isStart: false, isMiddle: false, isEnd: false };
    }

    // Convert times to minutes for comparison
    const slotTime = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
    const startTime = parseInt(slot.startTime.split(':')[0]) * 60 + parseInt(slot.startTime.split(':')[1]);
    const endTime = parseInt(slot.endTime.split(':')[0]) * 60 + parseInt(slot.endTime.split(':')[1]);

    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    };

    // prefer slot.reservationName if present, then assignedUserDetails.name, then for events use eventTitle
    const name = 
      slot.reservationName
      || slot.assignedUserDetails?.name
      || (slot.isEvent ? slot.eventTitle : '')
      || '';

    // and then:
    const details = {
      name,
      time: `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`,
      isRecurring: slot.isRecurring,
      isClass: slot.isClass,
      capacity: slot.capacity,
      price: slot.price,
      registeredMembers: slot.registeredMembers
    };

    return {
      status: slot.status,
      details,
      reservation: slot,
      isStart: slotTime === startTime,
      isMiddle: slotTime > startTime && slotTime < endTime - 1,
      isEnd: slotTime === endTime - 1,
      isEvent: slot.isEvent,
      isRecurring: slot.isRecurring,
      isClass: slot.isClass
    };
  }, [schedules]);

  const handleSlotClick = (courtId: string, time: string, existingReservation?: ScheduleSlot) => {
    if (existingReservation) {
      setSelectedSchedule(existingReservation);
      setSelectedTimeSlot(null);
    } else {
      setSelectedSchedule(null);
      setSelectedTimeSlot({ courtId, time });
    }
    setIsModalOpen(true);
  };

  const getRecommendedTimeSlots = useCallback((courtId: string) => {
    const recommendations: TimeSlotOption[] = [];
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    let startHour = currentHour;
    let startMinute = 
                     currentMinute < 30 ? 30 : 
                     currentMinute < 45 ? 45 : 0;
    if (currentMinute >= 45) {
      startHour += 1;
    }

    for (let i = 0; i < 8; i++) {
      const time = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
      const { status } = getSlotStatus(courtId, time);
      
      if (status === 'available') {
        recommendations.push({
          startTime: time,
          duration: '1hour',
          isRecommended: true
        });
      }
      
      startMinute += 30;
      if (startMinute >= 60) {
        startHour += 1;
        startMinute = 0;
      }
    }
    
    return recommendations;
  }, [getSlotStatus]);

  useEffect(() => {
    if (!loading && !scrollInitialized) {
      // Get current time in PST/PDT
      const now = new Date();
      // Convert to PST/PDT by getting the local time in that timezone
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      
      // Round to nearest hour
      const roundedHour = Math.round(currentHour + currentMinute/60);
      
      // Find the time slot element and scroll to it
      const timeSlotElement = document.querySelector(`[data-time="${roundedHour.toString().padStart(2, '0')}:00"]`);
      if (timeSlotElement) {
        timeSlotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      setScrollInitialized(true);
    }
  }, [loading, scrollInitialized]);

  // Add scroll event listener to prevent scrolling to past time slots
  useEffect(() => {
    const handleScroll = () => {
      const gridContainer = document.querySelector('.scheduler-grid-container');
      if (!gridContainer) return;

      const now = new Date();
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      // Find the first visible time slot
      const timeSlots = Array.from(document.querySelectorAll('.scheduler-time-slot'));
      let firstVisibleTimeSlot = null;
      
      for (const slot of timeSlots) {
        const rect = slot.getBoundingClientRect();
        if (rect.top >= 0) {
          firstVisibleTimeSlot = slot;
          break;
        }
      }

      if (firstVisibleTimeSlot) {
        const timeStr = firstVisibleTimeSlot.getAttribute('data-time');
        if (timeStr) {
          const [hours, minutes] = timeStr.split(':').map(Number);
          const slotTimeInMinutes = hours * 60 + minutes;
          
          // If trying to scroll to a past time, scroll back to current time
          if (slotTimeInMinutes < currentTimeInMinutes) {
            const currentTimeSlot = document.querySelector(`[data-time="${currentHour.toString().padStart(2, '0')}:${Math.floor(currentMinute / 30) * 30}"]`);
            if (currentTimeSlot) {
              currentTimeSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    };

    const gridContainer = document.querySelector('.scheduler-grid-container');
    if (gridContainer) {
      gridContainer.addEventListener('scroll', handleScroll);
      return () => gridContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Add current time indicator
  useEffect(() => {
    const updateCurrentTimeIndicator = () => {
      const now = new Date();
      const pstOffset = -7; // PST/PDT offset from UTC
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pstTime = new Date(utc + (3600000 * pstOffset));
      
      const currentHour = pstTime.getHours();
      const currentMinute = pstTime.getMinutes();
      const roundedMinute = Math.floor(currentMinute / 30) * 30;
      
      // Remove previous current time indicators
      document.querySelectorAll('.current-time-indicator').forEach(el => el.remove());
      
      // Add new current time indicator
      const timeSlot = document.querySelector(`[data-time="${currentHour.toString().padStart(2, '0')}:${roundedMinute.toString().padStart(2, '0')}"]`);
      if (timeSlot) {
        const indicator = document.createElement('div');
        indicator.className = 'current-time-indicator';
        indicator.style.position = 'absolute';
        indicator.style.left = '0';
        indicator.style.right = '0';
        indicator.style.height = '2px';
        indicator.style.backgroundColor = '#ef4444';
        indicator.style.zIndex = '30';
        timeSlot.appendChild(indicator);
      }
    };

    updateCurrentTimeIndicator();
    const interval = setInterval(updateCurrentTimeIndicator, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // When opening the calendar, sync calendarMonth to selectedDate
  useEffect(() => {
    if (showCalendar) {
      setCalendarMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }
  }, [showCalendar, selectedDate]);

  // Function to generate calendar days for the visible month
  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  // Get day names
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Function to handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowCalendar(false);
    // Invalidate the query for the new date
    queryClient.invalidateQueries({
      queryKey: ['schedules', facilityId, format(date, 'yyyy-MM-dd')]
    });
  };

  // Find the current business user from businessUsers or fallback to auth user
  const currentBusinessUser = useMemo(() => {
    if (!user?.uid) return null;
    let found = businessUsers.find(bu => bu.id === user.uid);
    if (!found && user.email) {
      found = businessUsers.find(bu => bu.email === user.email);
    }
    // If not found, fallback to auth user info
    if (!found && user) {
      return {
        id: user.uid,
        name: user.name || user.email || 'Business User',
        email: user.email || '',
        role: 'business',
        type: 'business' as const,
      };
    }
    if (found) {
      return {
        id: found.id,
        name: found.name,
        email: found.email,
        role: found.role,
        type: 'business' as const,
      };
    }
    return null;
  }, [user, businessUsers]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update the WebSocket handler
  useEffect(() => {
    const handleCourtUpdate = (data: any) => {
      if (data.action === 'created' || data.action === 'updated' || data.action === 'deleted') {
        // Create notification message
        const court = courts.find(c => c.id === data.reservation.courtId);
        const courtName = court?.name || 'a court';
        const isYou = data.reservation.assignedUserDetails?.id === currentBusinessUser?.id;
        const userName = isYou ? 'You' : (currentBusinessUser?.name || 'Someone');
        const time = new Date(data.reservation.date + 'T' + data.reservation.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        
        let message = '';
        switch (data.action) {
          case 'created':
            message = `New booking: ${courtName} at ${time} by ${userName}`;
            break;
          case 'updated':
            message = `Booking modified: ${courtName} at ${time} by ${userName}`;
            break;
          case 'deleted':
            message = `Booking cancelled: ${courtName} at ${time} by ${userName}`;
            break;
        }
        
        addNotification(message);

        // Update schedules in React Query cache with the correct date
        queryClient.setQueryData<ScheduleSlot[]>(['schedules', facilityId, data.reservation.date], (oldSchedules: ScheduleSlot[] = []) => {
          let newSchedules = [...oldSchedules];
          
          if (data.action === 'created') {
            newSchedules.push({
              ...data.reservation,
              assignedUserDetails: data.reservation.assignedUserDetails,
              className: 'schedule-slot-new'
            });
          } else if (data.action === 'updated') {
            // Remove the old reservation first
            newSchedules = newSchedules.filter(s => 
              !(s.courtId === data.reservation.courtId && 
                s.date === data.reservation.date && 
                s.startTime === data.reservation.startTime)
            );
            // Add the updated reservation
            newSchedules.push({
              ...data.reservation,
              assignedUserDetails: data.reservation.assignedUserDetails,
              className: 'schedule-slot-update'
            });
          } else if (data.action === 'deleted') {
            const index = newSchedules.findIndex(s =>
              s.courtId === data.reservation.courtId &&
              s.date === data.reservation.date &&
              s.startTime === data.reservation.startTime
            );
            if (index !== -1) {
              newSchedules[index] = {
                ...newSchedules[index],
                className: 'schedule-slot-delete'
              };
              setTimeout(() => {
                queryClient.setQueryData<ScheduleSlot[]>(['schedules', facilityId, data.reservation.date], (schedules = []) =>
                  (schedules as ScheduleSlot[]).filter((_: ScheduleSlot, i: number) => i !== index)
                );
              }, 300);
            }
          }
          return newSchedules;
        });
      }
    };
    websocketService.subscribe('court_update', handleCourtUpdate);
    return () => websocketService.unsubscribe('court_update', handleCourtUpdate);
  }, [facilityId, queryClient, courts, currentBusinessUser]);

  // Add CSS for transitions
  const styles = `
    .schedule-slot {
      transition: all 0.3s ease-in-out;
    }
    
    .schedule-slot-update {
      animation: updatePulse 0.3s ease-in-out;
    }
    
    .schedule-slot-new {
      animation: newPulse 0.3s ease-in-out;
    }
    
    .schedule-slot-delete {
      animation: deletePulse 0.3s ease-in-out;
    }
    
    @keyframes updatePulse {
      0% { background-color: rgba(147, 51, 234, 0.2); }
      100% { background-color: transparent; }
    }
    
    @keyframes newPulse {
      0% { background-color: rgba(34, 197, 94, 0.2); }
      100% { background-color: transparent; }
    }
    
    @keyframes deletePulse {
      0% { background-color: rgba(239, 68, 68, 0.2); }
      100% { background-color: transparent; }
    }
  `;

  // Add style tag to component
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Add a loading check for assignableUsers
  const assignableUsersLoading = assignableUsers.length === 0 && (businessUsers.length > 0 || staffUsers.length > 0 || members.length > 0);

  // Add debug log for NotificationCounter props
  // console.log('NotificationCounter notifications:', notifications);

  if (courtsLoading || schedulesLoading) {
    return <LoadingSpinner />;
  }

  // Restored scheduler with correct flex and height settings
  return (
    <div className="court-grid-scheduler flex-1 min-h-0 h-full flex flex-col">
      <NotificationCounter 
        notifications={notifications}
        onDismiss={dismissNotification}
        currentUserId={currentBusinessUser?.id}
      />
      <div className="scheduler-header bg-white shadow-sm p-4 flex-shrink-0">
        <div className="scheduler-date-navigation">
          <button onClick={() => setSelectedDate(subDays(selectedDate, 1))}>
            Previous Day
          </button>
          <div className="date-selector">
            <h2 onClick={() => setShowCalendar(!showCalendar)}>
              {format(selectedDate, 'MMMM d, yyyy')}
            </h2>
            {showCalendar && (
              <div className="calendar-popup">
                <div className="calendar-header flex items-center justify-between">
                  <button onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} className="px-2 py-1 text-lg">&#8592;</button>
                  <h3>{format(calendarMonth, 'MMMM, yyyy')}</h3>
                  <button onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} className="px-2 py-1 text-lg">&#8594;</button>
                </div>
                <div className="calendar-grid">
                  {/* Day names */}
                  {dayNames.map(day => (
                    <div key={day} className="calendar-day-name">
                      {day}
                    </div>
                  ))}
                  {/* Empty cells for days before the first of the month */}
                  {Array.from({ length: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay() }).map((_, index) => (
                    <div key={`empty-${index}`} className="calendar-day empty" />
                  ))}
                  {/* Calendar days */}
                  {calendarDays.map(day => {
                    const isSelected = format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <div
                        key={day.toISOString()}
                        className={`calendar-day${isSelected ? ' selected' : ''}${isToday ? ' today' : ''}`}
                        onClick={() => handleDateSelect(day)}
                      >
                        {format(day, 'd')}
                      </div>
                    );
                  })}
                </div>
                <div className="calendar-footer">
                  <button 
                    className="today-button"
                    onClick={() => handleDateSelect(new Date())}
                  >
                    Today
                  </button>
                </div>
              </div>
            )}
          </div>
          <button onClick={() => setSelectedDate(addDays(selectedDate, 1))}>
            Next Day
          </button>
        </div>
        <div className="scheduler-actions">
          <button 
            className="button button-primary"
            onClick={() => setShowNewReservationModal(true)}
          >
            New Reservation
          </button>
          <button
              className="button button-primary"
              onClick={() => setShowDateBlockerModal(true)}
            >
              Block Dates
          </button>
        </div>
      </div>

      <div className="scheduler-grid-container flex-1 min-h-0 h-full overflow-y-auto overflow-x-auto bg-white">
        <div className="scheduler-grid h-full min-h-0">
          <div className="scheduler-time-column">
            <div className="scheduler-time-header"></div>
            {TIME_SLOTS.map(time => (
              <div 
                key={time.value} 
                className="scheduler-time-slot"
                data-time={time.value}
              >
                {time.display}
              </div>
            ))}
          </div>

          <div className="scheduler-courts">
            {courts.map(court => (
              <div key={court.id} className="scheduler-court-column">
                <div className="scheduler-court-header">
                  <h3>{court.name}</h3>
                  <div className="scheduler-court-type">{court.type}</div>
                </div>
                {TIME_SLOTS.map((time, idx) => {
                  const { status, details, reservation, isStart, isMiddle, isEnd, isEvent, isRecurring, isClass } = getSlotStatus(court.id, time.value);
                  // Build base slot classes
                  const slotClassesArr = [
                    'scheduler-slot',
                    status,
                    isStart ? 'slot-start' : '',
                    isMiddle ? 'slot-middle' : '',
                    isEnd ? 'slot-end' : '',
                    isEvent ? 'event-slot' : '',
                    isRecurring ? 'recurring-slot' : '',
                    isClass ? 'class-slot' : '',
                    time.value === format(new Date(), 'HH:mm') ? 'current-time' : ''
                  ];
                  // Determine stacking order via z-index classes
                  if (reservation?.eventTitle === 'Blocked') {
                    slotClassesArr.push('z-30');
                  } else if (isEvent || isRecurring) {
                    slotClassesArr.push('z-20');
                  } else if (isClass) {
                    slotClassesArr.push('z-10');
                  } else {
                    slotClassesArr.push('z-0');
                  }
                  const slotClasses = slotClassesArr.filter(Boolean).join(' ');

                  if (time.isClosing) {
                    return (
                      <div
                        key={`${court.id}-${time.value}`}
                        className="scheduler-slot blocked closed-slot flex items-center justify-center text-gray-400 font-semibold"
                        data-time={time.value}
                        style={{ background: '#f3f4f6', color: '#9ca3af', fontStyle: 'italic' }}
                      >
                        Closed
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`${court.id}-${time.value}`}
                      className={slotClasses}
                      onClick={() => !isEvent && handleSlotClick(court.id, time.value, reservation || undefined)}
                      data-time={time.value}
                    >
                      {isStart && details && (
                        <div className="scheduler-slot-details">
                          <div className="scheduler-slot-details-name font-semibold">
                            {details.name}
                          </div>
                          <div className="scheduler-slot-details-time">{details.time}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showNewReservationModal && (
        usersLoading || assignableUsers.length === 0 ? (
          <LoadingSpinner />
        ) : (
          (() => { console.log('Assignable Users:', assignableUsers); return null; })(),
          <NewCourtReservationModal
            open={showNewReservationModal}
            onClose={() => setShowNewReservationModal(false)}
            date={selectedDate}
            courts={courts}
            assignableUsers={assignableUsers}
            selectedUser={currentBusinessUser?.id || ''}
            showAssignTo={true}
            onSave={async () => {
              await refetchSchedules();
              setShowNewReservationModal(false);
            }}
            recommendedTimeSlots={courts.length > 0 ? getRecommendedTimeSlots(courts[0].id) : []}
            selectedDuration={selectedDuration}
            onDurationChange={setSelectedDuration}
            businessHours={businessHours}
            businessId={businessId}
          />
        )
      )}

      {isModalOpen && (selectedTimeSlot || selectedSchedule) && (
        usersLoading || assignableUsers.length === 0 ? (
          <LoadingSpinner />
        ) : (
          (() => { console.log('Assignable Users:', assignableUsers); return null; })(),
          <NewCourtReservationModal
            open={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedSchedule(null);
              setSelectedTimeSlot(null);
            }}
            courtId={selectedSchedule?.courtId || selectedTimeSlot!.courtId}
            date={selectedDate}
            startTime={selectedSchedule?.startTime || selectedTimeSlot!.time}
            endTime={selectedSchedule?.endTime || format(
              new Date(selectedDate.setHours(
                parseInt(selectedTimeSlot!.time.split(':')[0]) + 1,
                parseInt(selectedTimeSlot!.time.split(':')[1])
              )),
              'HH:mm'
            )}
            courts={courts}
            assignableUsers={assignableUsers}
            selectedUser={currentBusinessUser?.id || ''}
            existingReservation={selectedSchedule}
            onSave={async () => {
              await refetchSchedules();
              setIsModalOpen(false);
              setSelectedSchedule(null);
              setSelectedTimeSlot(null);
            }}
            selectedDuration={selectedDuration}
            onDurationChange={setSelectedDuration}
            businessHours={businessHours}
            showAssignTo={true}
            businessId={businessId}
          />
        )
      )}

      {showDateBlockerModal && (
        <DateBlockerModal
          open={showDateBlockerModal}
          onClose={() => setShowDateBlockerModal(false)}
          facilityId={facilityId}
          businessId={businessId}
          courts={courts}
          onSave={async () => {
            await refetchSchedules();
          }}
          businessHours={businessHours}
        />
      )}
    </div>
  );
};

export default NewCourtScheduler; 